/**
 * ConfirmDialog - premium-styled, app-wide confirmation modal.
 *
 * Two surfaces:
 *  - useConfirm() - imperative hook returning Promise<boolean>
 *  - ConfirmProvider - wraps the app once, owns the modal portal
 *
 * Premium UX:
 *  - Glass panel with brand-colored top stripe (matches the CRM theme)
 *  - Spring-press buttons (.btn-*) so destructive actions feel deliberate
 *  - For tone='danger' the confirm button is replaced with a "press-and-hold"
 *    bar that fills over ~1.2s; releasing early cancels. This eliminates
 *    "oops I clicked Delete" mistakes and matches modern CRM safety patterns.
 *  - Escape always cancels (closing a confirm via Escape is universal UX)
 *  - Focus trap: opening dialog focuses the cancel button; on close we
 *    restore focus to whatever element opened it
 *  - Cross-fade backdrop, scale-in panel via existing keyframes
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export type ConfirmTone = 'danger' | 'warning' | 'info';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  /** Label of the confirm button. Default: 'Confirm'. */
  confirmLabel?: string;
  /** Label of the cancel button. Default: 'Cancel'. */
  cancelLabel?: string;
  /** Drives accent color + button variant. Default: 'danger'. */
  tone?: ConfirmTone;
  /** When true, type-to-confirm - user must type the supplied phrase. */
  requireText?: string;
  /**
   * For destructive actions, replace the click button with a press-and-hold
   * bar that must be held for `holdMs`. Defaults to true for `danger` tone,
   * false otherwise. Pass `false` to fall back to a plain click.
   */
  holdToConfirm?: boolean;
  /** Hold duration in ms. Default 1100. */
  holdMs?: number;
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const [typedPhrase, setTypedPhrase] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        lastFocused.current = (document.activeElement as HTMLElement) ?? null;
        setTypedPhrase('');
        setRequest({ ...opts, resolve });
      }),
    [],
  );

  const close = useCallback(
    (ok: boolean) => {
      if (!request) return;
      request.resolve(ok);
      setRequest(null);
      setTypedPhrase('');
      requestAnimationFrame(() => {
        try {
          lastFocused.current?.focus();
        } catch {
          // ignore
        }
      });
    },
    [request],
  );

  // Escape-to-cancel + autofocus the cancel button.
  // When `requireText` is supplied the user must explicitly type the phrase -
  // we still allow Escape to close because trapping it would frustrate users
  // far more than the small added safety. The docstring on this file is
  // updated to reflect this.
  useEffect(() => {
    if (!request) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
      }
    };
    document.addEventListener('keydown', onKey);
    requestAnimationFrame(() => cancelRef.current?.focus());
    return () => document.removeEventListener('keydown', onKey);
  }, [request, close]);

  const value = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm]);

  const tone: ConfirmTone = request?.tone ?? 'danger';
  const accent =
    tone === 'danger'
      ? 'from-rose-500 via-rose-500 to-pink-600'
      : tone === 'warning'
        ? 'from-amber-500 via-amber-500 to-orange-500'
        : 'from-brand-700 via-brand-500 to-cyan-400';
  const iconBg =
    tone === 'danger'
      ? 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300'
      : tone === 'warning'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
        : 'bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300';

  const requirePhraseOk =
    !request?.requireText || typedPhrase.trim() === request.requireText.trim();

  const useHoldBar =
    !!request &&
    !request.requireText && // type-to-confirm overrides the hold bar
    (request.holdToConfirm ?? tone === 'danger');

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {request && typeof document !== 'undefined'
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-dialog-title"
              className="modal-overlay"
            >
              <div className="modal-panel relative overflow-hidden">
                {/* Brand-colored top stripe - defines the tone at a glance */}
                <div
                  className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`}
                  aria-hidden="true"
                />
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full ${iconBg}`}
                    aria-hidden="true"
                  >
                    <ConfirmIcon tone={tone} />
                  </div>
                  <div className="min-w-0">
                    <h2 id="confirm-dialog-title" className="text-lg font-semibold leading-tight">
                      {request.title}
                    </h2>
                    {request.message ? (
                      <div className="text-sm text-slate-600 dark:text-slate-300 mt-1.5">
                        {request.message}
                      </div>
                    ) : null}
                  </div>
                </div>

                {request.requireText ? (
                  <div className="mb-5">
                    <label className="label mb-1.5">
                      Type{' '}
                      <span className="font-mono text-rose-600 dark:text-rose-300">
                        {request.requireText}
                      </span>{' '}
                      to confirm
                    </label>
                    <input
                      autoFocus
                      type="text"
                      className="input"
                      value={typedPhrase}
                      onChange={(e) => setTypedPhrase(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && requirePhraseOk) close(true);
                      }}
                    />
                  </div>
                ) : null}

                {useHoldBar ? (
                  <HoldToConfirmBar
                    label={request.confirmLabel ?? 'Hold to delete'}
                    holdMs={request.holdMs ?? 1100}
                    tone={tone}
                    onComplete={() => close(true)}
                    onCancel={() => close(false)}
                    cancelRef={cancelRef}
                    cancelLabel={request.cancelLabel ?? 'Cancel'}
                  />
                ) : (
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      ref={cancelRef}
                      type="button"
                      className="btn-secondary"
                      onClick={() => close(false)}
                    >
                      {request.cancelLabel ?? 'Cancel'}
                    </button>
                    <button
                      type="button"
                      className={
                        tone === 'danger'
                          ? 'btn-danger'
                          : tone === 'warning'
                            ? 'btn-primary'
                            : 'btn-primary'
                      }
                      onClick={() => close(true)}
                      disabled={!requirePhraseOk}
                    >
                      {request.confirmLabel ?? 'Confirm'}
                    </button>
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </ConfirmContext.Provider>
  );
}

// ─────────────────────────── Hold-to-confirm ───────────────────────────

function HoldToConfirmBar({
  label,
  holdMs,
  tone,
  onComplete,
  onCancel,
  cancelRef,
  cancelLabel,
}: {
  label: string;
  holdMs: number;
  tone: ConfirmTone;
  onComplete: () => void;
  onCancel: () => void;
  cancelRef: React.RefObject<HTMLButtonElement>;
  cancelLabel: string;
}) {
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  const stop = useCallback((commit: boolean) => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    startRef.current = null;
    setHolding(false);
    if (!commit) {
      setProgress(0);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const start = useCallback(() => {
    if (rafRef.current != null) return;
    setHolding(true);
    startRef.current = performance.now();
    const tick = (t: number): void => {
      if (startRef.current == null) return;
      const elapsed = t - startRef.current;
      const pct = Math.min(1, elapsed / holdMs);
      setProgress(pct);
      if (pct >= 1) {
        stop(true);
        onComplete();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [holdMs, onComplete, stop]);

  const fillClass =
    tone === 'danger'
      ? 'bg-gradient-to-r from-rose-500 via-rose-500 to-pink-600'
      : tone === 'warning'
        ? 'bg-gradient-to-r from-amber-500 to-orange-500'
        : 'bg-gradient-brand';

  return (
    <div className="space-y-2 pt-1">
      <button
        type="button"
        onPointerDown={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);
          start();
        }}
        onPointerUp={() => stop(false)}
        onPointerCancel={() => stop(false)}
        onPointerLeave={() => stop(false)}
        onKeyDown={(e) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            start();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') stop(false);
        }}
        className={`relative w-full overflow-hidden rounded-lg border border-slate-200/80
                    dark:border-white/10 bg-slate-100 dark:bg-slate-900/60
                    h-12 select-none cursor-pointer
                    transition-shadow focus:outline-none
                    focus-visible:ring-2 focus-visible:ring-rose-500/60
                    focus-visible:ring-offset-2 focus-visible:ring-offset-white
                    dark:focus-visible:ring-offset-slate-950 ${
                      holding ? 'shadow-[0_8px_24px_-8px_rgba(244,63,94,0.5)]' : ''
                    }`}
      >
        {/* Fill - driven by progress 0..1 */}
        <span
          className={`absolute inset-y-0 left-0 ${fillClass} transition-[width] duration-75 ease-out`}
          style={{ width: `${progress * 100}%` }}
          aria-hidden="true"
        />
        {/* Shine sweep while holding */}
        {holding && (
          <span
            className="absolute inset-0 pointer-events-none animate-pulse"
            style={{
              background:
                'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.25) 50%, transparent 65%)',
            }}
            aria-hidden="true"
          />
        )}
        {/* Label */}
        <span className="relative z-10 flex items-center justify-center w-full h-full text-sm font-semibold">
          <span
            className={
              progress > 0.5
                ? 'text-white drop-shadow-sm'
                : 'text-slate-700 dark:text-slate-200'
            }
          >
            {progress >= 1 ? '✓ Confirmed' : label}
            {progress > 0 && progress < 1 ? ` · ${Math.round(progress * 100)}%` : ''}
          </span>
        </span>
      </button>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500 dark:text-slate-400">
          Press and hold to confirm
        </span>
        <button
          ref={cancelRef}
          type="button"
          className="btn-ghost text-xs"
          onClick={onCancel}
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────── Icon ───────────────────────────

function ConfirmIcon({ tone }: { tone: ConfirmTone }) {
  if (tone === 'danger') {
    // Trash icon for destructive actions
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
      </svg>
    );
  }
  if (tone === 'warning') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

// ─────────────────────────── Hook ───────────────────────────

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be called inside <ConfirmProvider>. Wrap the app.');
  }
  return ctx.confirm;
}
